// Permissionless, Free-tier core probes (PRD §7). None of these read an API that triggers a
// permission prompt. Each reads from the injected RuntimeEnvironment / NetProbeContext, returns
// a structured measurement (blocked when the platform doesn't expose the signal), and scores
// pass/warn/fail. Authoritative scoring is server-side; score() here drives the client demo.

import { blocked, joinNotes, measured, viaNode } from '../types';
import type {
  Probe,
  ProbeMeasurement,
  ProbeOutcome,
  Thresholds,
} from '../types';
import type { ProbeRegistry } from '../registry';

const Category = {
  Network: 'Network & Connectivity',
  Browser: 'Browser',
  Device: 'Device & OS',
  Storage: 'Storage',
  Security: 'Security & Identity',
  Privacy: 'Privacy & Tracking',
  Capabilities: 'Capabilities',
  Media: 'Media & DRM',
} as const;

/** Informational probes always pass — they report a value, not a verdict. */
const info = (): ProbeOutcome => 'pass';

function scoreBoolean(m: ProbeMeasurement, good: boolean, onBad: ProbeOutcome): ProbeOutcome {
  if (m.blocked) {
    return 'warn';
  }
  return m.value === good ? 'pass' : onBad;
}

function scoreNumeric(m: ProbeMeasurement, thresholds: Thresholds | undefined, bound: 'max' | 'min'): ProbeOutcome {
  if (m.blocked || typeof m.value !== 'number') {
    return 'warn';
  }
  if (bound === 'max' && thresholds?.max !== undefined) {
    return m.value <= thresholds.max ? 'pass' : 'fail';
  }
  if (bound === 'min' && thresholds?.min !== undefined) {
    return m.value >= thresholds.min ? 'pass' : 'fail';
  }
  return 'pass';
}

const secureContext: Probe = {
  id: 'security.secure-context',
  category: Category.Security,
  tier: 'business',
  permissionRequired: false,
  run: (ctx) =>
    ctx.environment
      ? measured('security.secure-context', ctx.environment.secureContext)
      : blocked('security.secure-context', 'environment unavailable'),
  score: (m) => scoreBoolean(m, true, 'fail'),
};

const firstPartyCookies: Probe = {
  id: 'storage.first-party-cookies',
  category: Category.Storage,
  tier: 'business',
  permissionRequired: false,
  run: (ctx) =>
    ctx.environment
      ? measured('storage.first-party-cookies', ctx.environment.cookieEnabled)
      : blocked('storage.first-party-cookies', 'environment unavailable'),
  score: (m) => scoreBoolean(m, true, 'fail'),
};

const globalPrivacyControl: Probe = {
  id: 'privacy.gpc',
  category: Category.Privacy,
  tier: 'business',
  permissionRequired: false,
  run: (ctx) => {
    if (!ctx.environment) {
      return blocked('privacy.gpc', 'environment unavailable');
    }
    // Absent => the browser doesn't expose GPC; report it as not enabled.
    return measured('privacy.gpc', ctx.environment.globalPrivacyControl ?? false);
  },
  // Privacy posture: GPC on is good; a blocked read is a good score too (PRD §6.1).
  score: info, // privacy posture — informational by default; tenants opt in to a warn
};

/**
 * Picks the most specific real brand from a UA-CH brand list — skips GREASE decoys
 * ("Not/A)Brand"), and "Chromium" only wins when nothing more specific is present. Mirrors the
 * server's ClientIdentity so the displayed value reads "Google Chrome 148", not the noisy
 * "Not/A)Brand 99, Chromium 148". Returns null when no real brand is present.
 */
function pickBrand(brands: readonly { readonly brand: string; readonly version: string }[]): string | null {
  let specific: string | null = null;
  let chromium: string | null = null;
  for (const b of brands) {
    if (/not/i.test(b.brand.replace(/_/g, ' ')) && /brand/i.test(b.brand)) {
      continue; // GREASE decoy
    }
    const label = `${b.brand} ${b.version}`;
    if (/^chromium$/i.test(b.brand)) {
      chromium = label;
    } else if (!specific) {
      specific = label;
    }
  }
  return specific ?? chromium;
}

const browserIdentity: Probe = {
  id: 'browser.identity',
  category: Category.Browser,
  tier: 'free',
  permissionRequired: false,
  run: (ctx) => {
    const env = ctx.environment;
    if (!env) {
      return blocked('browser.identity', 'environment unavailable');
    }
    // UA Client Hints first (cleaned to the specific brand), with a UA-string fallback (UA reduction; PRD §7).
    const uaData = env.userAgentData;
    const brand = uaData ? pickBrand(uaData.brands) : null;
    const value = brand
      ? `${brand} / ${uaData!.platform}${uaData!.mobile ? ' (mobile)' : ''}`
      : env.userAgent;
    return measured('browser.identity', value);
  },
  score: info,
};

const deviceDisplay: Probe = {
  id: 'device.display',
  category: Category.Device,
  tier: 'free',
  permissionRequired: false,
  run: (ctx) => {
    const s = ctx.environment?.screen;
    return s
      ? measured('device.display', `${s.width}x${s.height} @${s.pixelRatio}x`)
      : blocked('device.display', 'screen unavailable');
  },
  score: info,
};

const timezoneLocale: Probe = {
  id: 'browser.timezone-locale',
  category: Category.Browser,
  tier: 'free',
  permissionRequired: false,
  run: (ctx) => {
    const env = ctx.environment;
    if (!env) {
      return blocked('browser.timezone-locale', 'environment unavailable');
    }
    return measured('browser.timezone-locale', `${env.timeZone ?? 'unknown'} / ${env.languages[0] ?? 'unknown'}`);
  },
  score: info,
};

const connectionInfo: Probe = {
  id: 'network.connection',
  category: Category.Network,
  tier: 'business',
  permissionRequired: false,
  run: (ctx) => {
    const c = ctx.environment?.connection;
    return c?.effectiveType
      ? measured('network.connection', c.effectiveType)
      : blocked('network.connection', 'Network Information API unavailable');
  },
  score: info,
};

const LATENCY_SAMPLES = 3;

const latency: Probe = {
  id: 'network.latency',
  category: Category.Network,
  tier: 'free',
  permissionRequired: false,
  run: async (ctx) => {
    const net = ctx.net;
    if (!net) {
      return blocked('network.latency', 'network context unavailable');
    }
    // Median of a few pings — steadier than a single sample, which a GC pause or TLS resume
    // can spike. A dropped sample is skipped; all dropped means the endpoint is unreachable.
    const samples: number[] = [];
    for (let i = 0; i < LATENCY_SAMPLES; i++) {
      const start = net.now();
      try {
        const response = await net.fetch(net.pingUrl, { method: 'GET', signal: ctx.signal });
        if (response.ok) {
          samples.push(net.now() - start);
        }
      } catch {
        // a dropped sample shouldn't sink the probe — keep going
      }
    }
    if (samples.length === 0) {
      return blocked('network.latency', 'ping unreachable');
    }
    samples.sort((a, b) => a - b);
    return measured('network.latency', Math.round(samples[Math.floor(samples.length / 2)]), viaNode(ctx));
  },
  score: (m, thresholds) => scoreNumeric(m, thresholds, 'max'),
};

// Adaptive download sizing: a small pilot estimates the line speed, then the main transfer
// is sized to run ~this long — fast lines get enough bytes to measure steady state instead
// of TCP slow-start, slow lines stop over-shooting (a fixed 5 MB took ~20 s at 2 Mbps).
const DOWNLOAD_BUDGET_MS = 2_500;
const DOWNLOAD_PILOT_BYTES = 512_000;
const DOWNLOAD_MIN_BYTES = 1_000_000;
const DOWNLOAD_MAX_BYTES = 32_000_000; // mirrors the server clamp

/** Mbps from a byte count over milliseconds. */
const toMbps = (bytes: number, elapsedMs: number): number => (bytes * 8) / (elapsedMs * 1000);

/** Bytes needed to sustain mbps for budgetMs (1e6 bits/s × s ÷ 8 bits/byte = ×125). */
const bytesForBudget = (mbps: number, budgetMs: number): number => Math.round(mbps * budgetMs * 125);

const download: Probe = {
  id: 'network.download',
  category: Category.Network,
  tier: 'free',
  permissionRequired: false,
  heavy: true,
  run: async (ctx) => {
    const net = ctx.net;
    if (!net) {
      return blocked('network.download', 'network context unavailable');
    }
    // Warm-up: prime DNS/TLS/TCP so the measured transfer isn't paying the handshake cost
    // (which would understate throughput on a fresh connection).
    try {
      await net.fetch(net.pingUrl, { method: 'GET', signal: ctx.signal });
    } catch {
      // a failed warm-up is fine — the measured request below is what counts
    }

    // Accurate path: stream-timed pilot → budget-sized stream-timed main transfer. A pilot
    // with elapsedMs 0 arrived in a single chunk ("faster than measurable at this size") —
    // go straight to the maximum transfer, which will chunk on any real connection.
    if (net.downloadTimed) {
      const pilot = await net.downloadTimed(DOWNLOAD_PILOT_BYTES, ctx.signal);
      if (pilot) {
        const targetBytes = pilot.elapsedMs <= 0
          ? DOWNLOAD_MAX_BYTES
          : Math.min(
              DOWNLOAD_MAX_BYTES,
              Math.max(DOWNLOAD_MIN_BYTES, bytesForBudget(toMbps(pilot.receivedBytes, pilot.elapsedMs), DOWNLOAD_BUDGET_MS)),
            );
        const main = await net.downloadTimed(targetBytes, ctx.signal);
        if (main && main.elapsedMs > 0) {
          const mbps = toMbps(main.receivedBytes, main.elapsedMs);
          return measured(
            'network.download',
            Math.round(mbps * 10) / 10,
            joinNotes(`${(main.receivedBytes / 1_000_000).toFixed(1)} MB sampled`, viaNode(ctx)),
          );
        }
      }
      // fall through: streaming unavailable — legacy measurement below
    }

    const start = net.now();
    const response = await net.fetch(net.downloadUrl, { method: 'GET', signal: ctx.signal });
    if (!response.ok) {
      return blocked('network.download', `download HTTP ${response.status}`);
    }
    await response.json().catch(() => undefined); // drain the body so timing covers transfer
    const seconds = (net.now() - start) / 1000;
    if (seconds <= 0) {
      return blocked('network.download', 'invalid timing');
    }
    const mbps = (net.downloadBytes * 8) / (seconds * 1_000_000);
    return measured('network.download', Math.round(mbps * 10) / 10, viaNode(ctx));
  },
  score: (m, thresholds) => scoreNumeric(m, thresholds, 'min'),
};

const canvas: Probe = {
  id: 'capabilities.canvas',
  category: Category.Capabilities,
  tier: 'business',
  permissionRequired: false,
  run: (ctx) => {
    const env = ctx.environment;
    if (!env || env.canvas2d === undefined) {
      return blocked('capabilities.canvas', 'canvas support unknown');
    }
    return measured('capabilities.canvas', env.canvas2d);
  },
  score: (m) => scoreBoolean(m, true, 'warn'),
};

const audioVideo: Probe = {
  id: 'media.audio-video',
  category: Category.Media,
  tier: 'business',
  permissionRequired: false,
  run: (ctx) => {
    const env = ctx.environment;
    if (!env || env.audioVideo === undefined) {
      return blocked('media.audio-video', 'media element support unknown');
    }
    return measured('media.audio-video', env.audioVideo);
  },
  score: (m) => scoreBoolean(m, true, 'warn'),
};

const fullscreen: Probe = {
  id: 'device.fullscreen',
  category: Category.Device,
  tier: 'business',
  permissionRequired: false,
  run: (ctx) => {
    const env = ctx.environment;
    if (!env || env.fullscreen === undefined) {
      return blocked('device.fullscreen', 'fullscreen support unknown');
    }
    return measured('device.fullscreen', env.fullscreen);
  },
  score: (m) => scoreBoolean(m, true, 'warn'),
};

const engine: Probe = {
  id: 'browser.engine',
  category: Category.Browser,
  tier: 'business',
  permissionRequired: false,
  run: (ctx) => {
    const ua = ctx.environment?.userAgent;
    if (!ua) {
      return blocked('browser.engine', 'environment unavailable');
    }
    // Order matters: Chromium UAs also contain "Safari", so test Blink before WebKit.
    const value = /Firefox\/|Gecko\/\d/.test(ua)
      ? 'Gecko'
      : /Edg\/|Chrome\/|Chromium\/|OPR\//.test(ua)
        ? 'Blink'
        : /Safari\//.test(ua)
          ? 'WebKit'
          : 'Unknown';
    return measured('browser.engine', value);
  },
  score: info,
};

const operatingSystem: Probe = {
  id: 'device.os',
  category: Category.Device,
  tier: 'free',
  permissionRequired: false,
  run: (ctx) => {
    const env = ctx.environment;
    if (!env) {
      return blocked('device.os', 'environment unavailable');
    }
    if (env.userAgentData?.platform) {
      return measured('device.os', env.userAgentData.platform);
    }
    const ua = env.userAgent;
    // Order matters (mirrors the server's ClientIdentity): iOS UAs contain "like Mac OS X"
    // and Android UAs contain "Linux", so test both before their desktop lookalikes. iPad
    // Safari requests desktop sites by default (UA reads "Macintosh") — a Mac-looking UA
    // with a touchscreen is an iPad, not a Mac.
    const os = /iPhone|iPod/.test(ua)
      ? 'iOS'
      : /iPad/.test(ua)
        ? 'iPadOS'
        : /Android/.test(ua)
          ? 'Android'
          : /Windows/.test(ua)
            ? 'Windows'
            : /Mac OS X|Macintosh/.test(ua)
              ? (env.maxTouchPoints ?? 0) > 1
                ? 'iPadOS'
                : 'macOS'
              : /CrOS/.test(ua)
                ? 'ChromeOS'
                : /Linux/.test(ua)
                  ? 'Linux'
                  : 'Unknown';
    return measured('device.os', os);
  },
  score: info,
};

const touchPointer: Probe = {
  id: 'device.touch-pointer',
  category: Category.Device,
  tier: 'business',
  permissionRequired: false,
  run: (ctx) => {
    const env = ctx.environment;
    if (!env || env.maxTouchPoints === undefined) {
      return blocked('device.touch-pointer', 'pointer info unavailable');
    }
    const pointer = env.finePointer ? 'fine pointer' : 'coarse pointer';
    const value =
      env.maxTouchPoints > 0 ? `touch (${env.maxTouchPoints} points), ${pointer}` : `no touch, ${pointer}`;
    return measured('device.touch-pointer', value);
  },
  score: info,
};

const css: Probe = {
  id: 'capabilities.css',
  category: Category.Capabilities,
  tier: 'business',
  permissionRequired: false,
  run: (ctx) => {
    const env = ctx.environment;
    if (!env || env.cssModern === undefined) {
      return blocked('capabilities.css', 'CSS support unknown');
    }
    // On failure, name the missing baseline feature(s) for support triage (e.g. "missing :has()").
    const note = !env.cssModern && env.cssMissing && env.cssMissing.length > 0
      ? `missing ${env.cssMissing.join(', ')}`
      : undefined;
    return measured('capabilities.css', env.cssModern, note);
  },
  score: (m) => scoreBoolean(m, true, 'warn'),
};

const localStore: Probe = {
  id: 'storage.local',
  category: Category.Storage,
  tier: 'business',
  permissionRequired: false,
  run: (ctx) => {
    const env = ctx.environment;
    if (!env || env.localStorage === undefined) {
      return blocked('storage.local', 'storage support unknown');
    }
    return measured('storage.local', env.localStorage);
  },
  score: (m) => scoreBoolean(m, true, 'warn'),
};

const doNotTrack: Probe = {
  id: 'privacy.dnt',
  category: Category.Privacy,
  tier: 'business',
  permissionRequired: false,
  run: (ctx) => {
    if (!ctx.environment) {
      return blocked('privacy.dnt', 'environment unavailable');
    }
    return measured('privacy.dnt', ctx.environment.doNotTrack ?? false);
  },
  // Privacy posture (mirrors GPC): DNT on is good; absent is the common default.
  score: info, // privacy posture — informational by default; tenants opt in to a warn
};

/**
 * The permissionless probe set (no permission prompts). Tiers are mixed: six free probes
 * plus the previously-free probes re-gated to Business by FreeTierTrim. The engine `tier`
 * here mirrors the server catalog's MinimumPlan; the server gating remains authoritative.
 */
const publicIp: Probe = {
  id: 'network.public-ip',
  category: Category.Network,
  tier: 'free',
  permissionRequired: false,
  run: async (ctx) => {
    if (!ctx.ip) {
      return blocked('network.public-ip', 'IP context unavailable');
    }
    const result = await ctx.ip.address();
    if (!result?.ip) {
      return blocked('network.public-ip', 'public address unavailable');
    }
    // The visitor sees their own full address live; the server masks it at ingestion
    // before anything is stored or shared (PRD §16).
    return measured('network.public-ip', result.version ? `${result.ip} (${result.version})` : result.ip);
  },
  // Informational: the address is identity, not a verdict.
  score: info,
};

export const coreProbes: readonly Probe[] = [
  secureContext,
  firstPartyCookies,
  globalPrivacyControl,
  browserIdentity,
  deviceDisplay,
  timezoneLocale,
  connectionInfo,
  latency,
  download,
  publicIp,
  canvas,
  audioVideo,
  fullscreen,
  engine,
  operatingSystem,
  touchPointer,
  css,
  localStore,
  doNotTrack,
];

/** Registers every core probe into the given registry. */
export function registerCoreProbes(target: ProbeRegistry): void {
  for (const probe of coreProbes) {
    target.register(probe);
  }
}
